import { useEffect, useState, useRef, useCallback, useLayoutEffect, useMemo, lazy, Suspense } from "react";
import { VISOR_MODULE_BREADCRUMB_ROOT } from "@/modules/workModuleCore";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtMoney, fmtNumber, formatApiError, detailFromAxiosError, tipoColor, tipoLabel, visorCfdiTipoLabel } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { PageHeader } from "./layoutChrome";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { FileDown, Trash2, Loader2, FileX2, Filter, CircleCheckBig, CircleX, Link2, ChevronRight, ChevronDown, CalendarRange, Ban, AlertTriangle, CheckCircle2, FileSearch, FileSpreadsheet, GitCompare, TrendingDown, UsersRound, Scale, LayoutGrid, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { ModuleFeatureCard } from "@/components/HubModuleCard";
import { clearVisorReturnTo, isConfrontaReturnPath, peekVisorReturnTo } from "@/lib/satVisorNavigation";
import { satExpandChevronCn, satExpandMainCn, satExpandRowCn, satExpandToggleCn } from "@/lib/expandableSatRail";
import ExportToolbar from "@/components/ExportToolbar";
import { IdentityValidateButton } from "@/components/identity/IdentityCheckDialog";
import { currentYearToTodayRangeIso } from "@/lib/dateMx";

const OPEN_SECTION_SIMILAR_SAT = "visores-similar-sat";

/** Listado principal de CFDI del visor XML: no cargar en Reglas ni en la sección embebida Similar SAT. */
function shouldLoadVisorXmlCfdiList(openSection, isSatFixedVisor, panelActive = true) {
  if (!panelActive) return false;
  if (isSatFixedVisor) return true;
  if (!openSection) return false;
  if (openSection === "reglas") return false;
  if (openSection === OPEN_SECTION_SIMILAR_SAT) return false;
  return true;
}

const satPeriodRange = (year, satMes) => {
  if (satMes === "all") {
    return { from: `${year}-01-01`, to: `${year}-12-31` };
  }
  return monthRangeFromYm(`${year}-${satMes}`);
};

const VisoresSatHubBody = lazy(() => import("./VisoresSatHubBody"));

/** Cabeceras del visor XML (ruta /visor): solo la flecha expande; el título no es interactivo. */
const VISOR_MAIN_TABS = [
  { value: "emitido", label: "Visor de CFDI Emitidos", Icon: GitCompare, testid: "tab-emitido" },
  { value: "recibido", label: "Visor de CFDI Recibidos", Icon: TrendingDown, testid: "tab-recibido" },
  { value: "nomina", label: "Visor de CFDI Nómina", Icon: UsersRound, testid: "tab-nomina" },
];

/** Tarjetas del hub /visor (Opción A: misma ruta, misma lógica de carga lazy). */
const VISOR_HUB_SECTIONS = [
  {
    id: "reglas",
    label: "Reglas y límites (NC, sustitución, pagos parciales)",
    description: "Criterios del listado, notas de crédito, complementos PPD/PUE y confronta.",
    Icon: Scale,
    testid: "visor-card-reglas",
  },
  {
    id: "emitido",
    label: "Visor de CFDI Emitidos",
    description: "Comprobantes que emitió el cliente seleccionado, por fecha de timbrado.",
    Icon: GitCompare,
    testid: "visor-card-emitido",
  },
  {
    id: "recibido",
    label: "Visor de CFDI Recibidos",
    description: "Facturas y gastos recibidos por el cliente (tipo I, dirección recibido).",
    Icon: TrendingDown,
    testid: "visor-card-recibido",
  },
  {
    id: "nomina",
    label: "Visor de CFDI Nómina",
    description: "Recibos de nómina timbrados del cliente.",
    Icon: UsersRound,
    testid: "visor-card-nomina",
  },
  {
    id: OPEN_SECTION_SIMILAR_SAT,
    label: "Visores CFDI (Similar SAT)",
    description: "Tres visores tipo portal SAT: emitidos, recibidos y nóminas por periodo.",
    Icon: LayoutGrid,
    testid: "visor-card-similar-sat",
  },
];

const VISOR_OPEN_QUERY_MAP = {
  ingresos: "emitido",
  egresos: "recibido",
  nominas: "nomina",
};

const formatPagoLabel = (mp) => {
  if (!mp) return "—";
  return mp; // PUE / PPD
};

const fmtSyncAt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return String(iso);
  }
};

const MONTH_NAMES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const monthLabelFromYm = (ym) => {
  const [y, m] = String(ym || "").split("-");
  const monthIdx = Number(m) - 1;
  if (!y || monthIdx < 0 || monthIdx > 11) return ym || "—";
  return `${MONTH_NAMES[monthIdx]} ${y}`;
};

const monthRangeFromYm = (ym) => {
  const [y, m] = String(ym || "").split("-").map(Number);
  if (!y || !m) return { from: "", to: "" };
  const yy = String(y).padStart(4, "0");
  const mm = String(m).padStart(2, "0");
  const lastDay = new Date(y, m, 0).getDate();
  return {
    from: `${yy}-${mm}-01`,
    to: `${yy}-${mm}-${String(lastDay).padStart(2, "0")}`,
  };
};

/** YYYY-MM del CFDI para filtrar filas al elegir un mes en el resumen. */
const cfdiYmFromRow = (row) => {
  const raw =
    row?.tipo_comprobante === "P"
      ? row?.fecha_pago || row?.fecha
      : row?.fecha;
  return (raw || "").slice(0, 7);
};

/** Une listados por id (sin duplicar UUID). */
const mergeVisorSatPeriodItems = (primary, payments) => {
  const byId = new Map();
  for (const row of [...(primary || []), ...(payments || [])]) {
    if (row?.id) byId.set(row.id, row);
  }
  return [...byId.values()].sort((a, b) => {
    const fa = a.tipo_comprobante === "P" ? (a.fecha_pago || a.fecha || "") : (a.fecha || "");
    const fb = b.tipo_comprobante === "P" ? (b.fecha_pago || b.fecha || "") : (b.fecha || "");
    return fb.localeCompare(fa);
  });
};

const sortCfdiRowsByRelevantDateDesc = (rows) =>
  [...(rows || [])].sort((a, b) => {
    const fa = a.tipo_comprobante === "P" ? (a.fecha_pago || a.fecha || "") : (a.fecha || "");
    const fb = b.tipo_comprobante === "P" ? (b.fecha_pago || b.fecha || "") : (b.fecha || "");
    return fb.localeCompare(fa);
  });

async function openCfdiPdfInNewTab(cfdiId) {
  const popup = window.open("", "_blank");
  if (!popup) {
    toast.error("Habilita ventanas emergentes para ver el PDF");
    return;
  }
  try {
    const { data } = await api.get(`/cfdi/${cfdiId}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    popup.location.href = url;
    // Revocamos después de un tiempo razonable para no romper la vista inmediata.
    setTimeout(() => URL.revokeObjectURL(url), 180000);
  } catch (e) {
    try { popup.close(); } catch {}
    const msg = (await detailFromAxiosError(e)) || formatApiError(e.response?.data?.detail) || "No se pudo cargar el PDF";
    toast.error(msg);
  }
}

/** path debe ser relativo al baseURL de axios (ej. `/reports/cfdi-excel?...`), nunca `API + path`. */
async function downloadApiFile(path, filename) {
  try {
    const rel = path.startsWith("/") ? path : `/${path}`;
    const { data } = await api.get(rel, { responseType: "blob" });
    const objectUrl = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 180000);
  } catch (e) {
    const msg = (await detailFromAxiosError(e)) || formatApiError(e.response?.data?.detail) || "No se pudo descargar el archivo";
    toast.error(msg);
  }
}

/** Etiquetas opcionales del carril cuando el visor va embebido (p. ej. hub Similar SAT); no afecta la ruta /visor. */
export default function VisorCFDI({
  defaultView = "emitido",
  forceSatPeriodLayout = false,
  embedMode = false,
  embedSectionLabels = null,
  /** En acordeón embebido: solo consultar API cuando la sección está abierta. */
  panelActive = true,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [year, setYear] = useState(() => {
    const saved = Number(localStorage.getItem("vf:selected_year") || 0);
    if (saved >= 2020 && saved <= 2099) return saved;
    const now = new Date();
    return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
  });
  const [health, setHealth] = useState(null);
  const [view, setView] = useState(defaultView); // 'emitido' | 'recibido' | 'nomina'
  /** En /visor (sin vista fija): null = hub de tarjetas hasta elegir sección. */
  const [openSection, setOpenSection] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState("all"); // all|pue|ppd|cobrados
  const [fechaDesde, setFechaDesde] = useState(() => currentYearToTodayRangeIso().from);
  const [fechaHasta, setFechaHasta] = useState(() => currentYearToTodayRangeIso().to);
  const [fechaField, setFechaField] = useState("fecha"); // 'fecha' (emisión) | 'fecha_pago'
  const [yearMode, setYearMode] = useState("year"); // 'year' = filter by year, 'all' = cross-year
  /** Visores SAT (rutas dedicadas): periodo como el portal SAT — mes natural o todo el año. */
  const [satMes, setSatMes] = useState("all"); // "all" | "01".."12"
  const [excluirCancelados, setExcluirCancelados] = useState(true);
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  const [bulkUuids, setBulkUuids] = useState("");
  const [bulkEstado, setBulkEstado] = useState("Cancelado");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [fiscalSummary, setFiscalSummary] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthPayments, setMonthPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [syncMeta, setSyncMeta] = useState(null);
  const [focusRowUuid, setFocusRowUuid] = useState("");
  const focusHandledRef = useRef("");
  const loadRequestRef = useRef(0);

  // Nómina-only view: always emitido + tipo N. Used for the nomina tab.
  const isNominaView = view === "nomina";
  const direccion = isNominaView ? "emitido" : view;
  // Facturas recibidas (gastos) son tipo I con direccion=recibido, no tipo E (NC). Igual que /confronta/egresos.
  const activeTipo = isNominaView ? "N" : "I";
  const fixedView =
    forceSatPeriodLayout || ["/visor-ingresos", "/visor-egresos", "/visor-nominas"].includes(location.pathname)
      ? defaultView
      : null;
  const isSatFixedVisor = fixedView !== null;
  /** Emitidos/recibidos en /visor y Similar SAT: resumen PUE + complementos y filtros de cobro. */
  const useCobradosLayout =
    isSatFixedVisor || (!isNominaView && (openSection === "emitido" || openSection === "recibido"));
  /** Carga tipo portal SAT (facturas + complementos) cuando no hay rango manual de fechas. */
  const useSatPeriodLoading =
    isSatFixedVisor || (useCobradosLayout && yearMode === "year" && !fechaDesde && !fechaHasta);

  const sectionLabel = useCallback(
    (viewKey) => {
      const raw = embedSectionLabels?.[viewKey];
      if (typeof raw === "string" && raw.trim()) return raw.trim();
      return VISOR_MAIN_TABS.find((t) => t.value === viewKey)?.label ?? viewKey;
    },
    [embedSectionLabels]
  );

  const openVisorSection = useCallback((sectionId) => {
    if (VISOR_MAIN_TABS.some((t) => t.value === sectionId)) {
      setOpenSection(sectionId);
      setView(sectionId);
      setExcluirCancelados(true);
    } else {
      setOpenSection(sectionId);
    }
  }, []);

  const activeHubSection = VISOR_HUB_SECTIONS.find((s) => s.id === openSection);

  useEffect(() => {
    if (fixedView || embedMode) return;
    const o = (searchParams.get("open") || "").toLowerCase();
    const mapped = VISOR_OPEN_QUERY_MAP[o];
    if (mapped) {
      openVisorSection(mapped);
    }
    if (searchParams.get("export") === "1" && !mapped) {
      openVisorSection("emitido");
    }
  }, [searchParams, fixedView, embedMode, openVisorSection]);

  useEffect(() => {
    if (fixedView && view !== fixedView) {
      setView(fixedView);
    }
  }, [fixedView, view]);

  // Plegado al montar o al cambiar entrada del historial; evita flash y restablece tras bfcache.
  useLayoutEffect(() => {
    if (!fixedView) {
      setOpenSection(null);
    }
  }, [location.key, location.pathname, fixedView]);

  useEffect(() => {
    if (fixedView) return;
    const onPageShow = (e) => {
      if (e.persisted) {
        setOpenSection(null);
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [fixedView]);

  useEffect(() => {
    if (isSatFixedVisor) {
      setYearMode("year");
    }
  }, [isSatFixedVisor]);

  useEffect(() => {
    if (!useCobradosLayout && (paymentStatus === "p" || paymentStatus === "cobrados")) {
      setPaymentStatus("all");
      setFechaField("fecha");
    }
  }, [useCobradosLayout, openSection, paymentStatus]);

  useEffect(() => {
    api.get("/clients").then(({ data }) => {
      setClients(data);
    });
  }, []);

  useEffect(() => {
    if (!clientId) {
      setSyncMeta(null);
      return;
    }
    api.get("/cfdi/client-sync-meta", { params: { client_id: clientId } })
      .then(({ data }) => setSyncMeta(data))
      .catch(() => setSyncMeta(null));
  }, [clientId]);

  const load = useCallback(async (override = {}) => {
    if (!clientId) return;
    const cfdiPanelOpen = shouldLoadVisorXmlCfdiList(openSection, isSatFixedVisor || useCobradosLayout, panelActive);
    if (!cfdiPanelOpen) {
      setItems([]);
      setFiscalSummary(null);
      setMonthlySummary(null);
      setLoading(false);
      return;
    }
    const reqId = ++loadRequestRef.current;
    const effectiveSatMes = override.satMes !== undefined ? override.satMes : satMes;
    const effectiveFechaDesde = override.fechaDesde !== undefined ? override.fechaDesde : fechaDesde;
    const effectiveFechaHasta = override.fechaHasta !== undefined ? override.fechaHasta : fechaHasta;
    setLoading(true);
    try {
      const excluirVigentesSat = excluirCancelados;
      const params = { client_id: clientId, direccion, tipo: activeTipo };
      if (useSatPeriodLoading) {
        params.fecha_field = "fecha";
        const rng = isSatFixedVisor
          ? satPeriodRange(year, effectiveSatMes)
          : { from: `${year}-01-01`, to: `${year}-12-31` };
        params.fecha_desde = rng.from;
        params.fecha_hasta = rng.to;
      } else {
        params.fecha_field = fechaField;
        if (yearMode === "year") params.year = year;
        if (!isNominaView && paymentStatus !== "all") params.payment_status = paymentStatus;
        if (effectiveFechaDesde) params.fecha_desde = effectiveFechaDesde;
        if (effectiveFechaHasta) params.fecha_hasta = effectiveFechaHasta;
      }
      if (excluirVigentesSat) params.excluir_cancelados = "true";
      let list;
      let paymentList = [];
      if (useSatPeriodLoading) {
        const periodRng = isSatFixedVisor
          ? satPeriodRange(year, effectiveSatMes)
          : { from: `${year}-01-01`, to: `${year}-12-31` };
        const paymentParams = {
          client_id: clientId,
          direccion,
          tipo: "P",
          fecha_field: "fecha_pago",
          payment_status: "p",
          fecha_desde: periodRng.from,
          fecha_hasta: periodRng.to,
          excluir_cancelados: excluirVigentesSat ? "true" : "false",
        };
        if (isNominaView) {
          params.payment_status = "pue_n";
          paymentParams.solo_pagos_nomina_ppd = "true";
        }
        const primaryRequest = api.get("/cfdi/list", { params });
        const summaryParams = {
          client_id: clientId,
          direccion,
          fecha_field: "fecha",
          fecha_desde: periodRng.from,
          fecha_hasta: periodRng.to,
          excluir_cancelados: excluirVigentesSat ? "true" : "false",
        };
        if (isNominaView) summaryParams.tipo = "N";
        const [primaryRes, paymentRes, summaryRes] = await Promise.all([
          primaryRequest.then((r) => sortCfdiRowsByRelevantDateDesc(r.data || [])),
          api.get("/cfdi/list", { params: paymentParams }),
          api.get("/cfdi/fiscal-summary", { params: summaryParams }),
        ]);
        list = primaryRes;
        paymentList = paymentRes.data || [];
        setFiscalSummary(summaryRes.data);
      } else {
        // Visor XML (Emitidos / Recibidos / Nómina): solo comprobantes por fecha de emisión, sin tipo P.
        const xmlPayment =
          paymentStatus === "p" || paymentStatus === "cobrados" ? "all" : paymentStatus;
        const listParams = { ...params };
        if (xmlPayment !== "all") listParams.payment_status = xmlPayment;
        else delete listParams.payment_status;
        listParams.fecha_field = "fecha";
        if (isNominaView) listParams.tipo = "N";
        const summaryParams = {
          client_id: clientId,
          direccion,
          tipo: activeTipo,
          fecha_field: "fecha",
          excluir_cancelados: excluirCancelados ? "true" : "false",
        };
        if (yearMode === "year") summaryParams.year = year;
        if (effectiveFechaDesde) summaryParams.fecha_desde = effectiveFechaDesde;
        if (effectiveFechaHasta) summaryParams.fecha_hasta = effectiveFechaHasta;
        const [primaryRes, summaryRes] = await Promise.all([
          api.get("/cfdi/list", { params: listParams }).then((r) =>
            sortCfdiRowsByRelevantDateDesc(r.data || [])
          ),
          api.get("/cfdi/fiscal-summary", { params: summaryParams }),
        ]);
        list = primaryRes;
        setFiscalSummary(summaryRes.data);
      }
      if (reqId !== loadRequestRef.current) return;
      setItems(list);
      setMonthPayments(selectedMonth ? paymentList : []);
    } catch (e) {
      const msg =
        (e && e.code === "ERR_NETWORK") || (e && e.message === "Network Error")
          ? "No se pudo conectar con el servidor. Verifica que el backend esté activo (puerto 8000) y reinicia el front (yarn start) si acabas de cambiar .env."
          : (await detailFromAxiosError(e)) || formatApiError(e.response?.data?.detail) || "Error al cargar el visor";
      toast.error(msg);
      if (reqId !== loadRequestRef.current) return;
      setItems([]);
      setMonthPayments([]);
      setFiscalSummary(null);
    } finally {
      if (reqId === loadRequestRef.current) setLoading(false);
    }
  }, [
    clientId,
    year,
    yearMode,
    paymentStatus,
    fechaDesde,
    fechaHasta,
    fechaField,
    excluirCancelados,
    isSatFixedVisor,
    satMes,
    isNominaView,
    direccion,
    activeTipo,
    openSection,
    selectedMonth,
    panelActive,
    useSatPeriodLoading,
  ]);

  const loadMonthlySummary = useCallback(async () => {
    if (!clientId || yearMode !== "year") {
      setMonthlySummary(null);
      return;
    }
    const cfdiPanelOpen = shouldLoadVisorXmlCfdiList(openSection, isSatFixedVisor || useCobradosLayout, panelActive);
    if (!cfdiPanelOpen) {
      setMonthlySummary(null);
      setMonthlyLoading(false);
      return;
    }
    setMonthlyLoading(true);
    try {
      const params = {
        client_id: clientId,
        direccion,
        year,
        tipo: activeTipo,
      };
      if (useCobradosLayout) params.fecha_field = "fecha";
      if (!isNominaView && !useCobradosLayout && paymentStatus !== "all") params.payment_status = paymentStatus;
      if (excluirCancelados) params.excluir_cancelados = "true";
      const path = useCobradosLayout
        ? "/cfdi/visor-monthly-summary"
        : "/cfdi/visor-xml-monthly-summary";
      const { data } = await api.get(path, { params });
      setMonthlySummary(data);
    } catch (e) {
      setMonthlySummary(null);
    } finally {
      setMonthlyLoading(false);
    }
  }, [
    clientId,
    year,
    yearMode,
    direccion,
    isSatFixedVisor,
    useCobradosLayout,
    isNominaView,
    paymentStatus,
    excluirCancelados,
    activeTipo,
    openSection,
    panelActive,
  ]);

  useEffect(() => {
    loadMonthlySummary();
  }, [loadMonthlySummary]);

  const clearFocusParams = useCallback(() => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete("focusUuid");
      p.delete("focusYear");
      p.delete("focusMes");
      p.delete("focusClient");
      return p;
    }, { replace: true });
  }, [setSearchParams]);
  const closeDetail = useCallback(() => {
    const fromState = String(location.state?.returnTo || "").trim();
    const fromStorage = peekVisorReturnTo().trim();
    clearVisorReturnTo();
    const back =
      (isConfrontaReturnPath(fromState) && fromState) ||
      (isConfrontaReturnPath(fromStorage) && fromStorage) ||
      "";
    setDetail(null);
    clearFocusParams();
    if (back) {
      navigate(back, { replace: true });
    }
  }, [location.state, navigate, clearFocusParams]);

  useEffect(() => {
    const fu = (searchParams.get("focusUuid") || "").trim().toUpperCase();
    if (!fu) return;
    const fc = (searchParams.get("focusClient") || "").trim();
    if (fc && clients.length > 0 && clients.some((c) => c.id === fc) && clientId !== fc) {
      setClientId(fc);
    }
    const fy = searchParams.get("focusYear");
    const fm = searchParams.get("focusMes");
    if (fy) {
      const yn = parseInt(fy, 10);
      if (!Number.isNaN(yn)) setYear(yn);
    }
    if (isSatFixedVisor) {
      if (fm && fm !== "all") setSatMes(fm);
      else setSatMes("all");
    }
  }, [searchParams, clients, clientId, setClientId, isSatFixedVisor, fixedView, defaultView]);

  useEffect(() => {
    const fu = (searchParams.get("focusUuid") || "").trim().toUpperCase();
    if (!fu || !clientId) {
      focusHandledRef.current = "";
      return;
    }
    if (loading) return;
    if (focusHandledRef.current === fu) return;

    const match = items.find((i) => (i.uuid || "").toUpperCase() === fu);
    if (match) {
      focusHandledRef.current = fu;
      setFocusRowUuid(fu);
      setDetail(match);
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-testid="cfdi-row-${fu.slice(0, 8)}"]`);
        el?.scrollIntoView({ block: "center", behavior: "smooth" });
      });
      clearFocusParams();
      setTimeout(() => setFocusRowUuid(""), 10000);
      return;
    }

    focusHandledRef.current = fu;
    clearFocusParams();
    setTimeout(() => setFocusRowUuid(""), 10000);
    (async () => {
      try {
        const { data } = await api.get("/cfdi/by-uuid", { params: { client_id: clientId, uuid: fu } });
        setDetail(data);
        if (items.length > 0) {
          toast.info(
            "El CFDI no está en la tabla con el filtro actual; se abrió el detalle. Ajusta mes o pestaña si necesitas verlo en el listado."
          );
        }
      } catch {
        toast.error("No se encontró el CFDI para este cliente.");
      }
    })();
  }, [items, loading, clientId, searchParams, clearFocusParams]);

  // Diagnóstico de integridad de CFDIs cargados (detecta qué XMLs necesitan re-upload)
  useEffect(() => {
    if (!clientId) { setHealth(null); return; }
    api.get("/cfdi/health", { params: { client_id: clientId, year } })
      .then(({ data }) => setHealth(data))
      .catch(() => setHealth(null));
  }, [clientId, year]);

  const clearAll = async () => {
    if (!window.confirm(`¿Eliminar TODOS los CFDI ${direccion}s del año ${year}?`)) return;
    await api.delete("/cfdi/clear", { params: { client_id: clientId, year, direccion }});
    toast.success("CFDI eliminados");
    load();
  };

  const downloadExcel = () => {
    if (!clientId) {
      toast.error("Selecciona un cliente para exportar");
      return;
    }
    const params = new URLSearchParams({ client_id: clientId, direccion });
    params.append("tipo", activeTipo);
    params.append("fecha_field", isSatFixedVisor ? "fecha" : fechaField);
    if (isSatFixedVisor) {
      if (satMes === "all") params.append("year", String(year));
      else {
        const rng = monthRangeFromYm(`${year}-${satMes}`);
        params.append("fecha_desde", rng.from);
        params.append("fecha_hasta", rng.to);
      }
      params.append("excluir_cancelados", "true");
    } else {
      if (yearMode === "year") params.append("year", String(year));
      if (!isNominaView && paymentStatus !== "all") params.append("payment_status", paymentStatus);
      if (fechaDesde) params.append("fecha_desde", fechaDesde);
      if (fechaHasta) params.append("fecha_hasta", fechaHasta);
      if (excluirCancelados) params.append("excluir_cancelados", "true");
    }
    downloadApiFile(`/reports/cfdi-excel?${params.toString()}`, `CFDI_${direccion}_${year}.xlsx`);
  };

  const downloadMonthlyExcel = () => {
    if (!clientId) {
      toast.error("Selecciona un cliente para exportar");
      return;
    }
    if (yearMode !== "year") {
      toast.error("Selecciona un año para exportar el resumen mensual");
      return;
    }
    const params = new URLSearchParams({
      client_id: clientId,
      direccion,
      year: String(year),
      fecha_field: useCobradosLayout ? "fecha" : fechaField,
    });
    params.append("tipo", activeTipo);
    if (!isNominaView && !useCobradosLayout && paymentStatus !== "all") params.append("payment_status", paymentStatus);
    if (excluirCancelados) params.append("excluir_cancelados", "true");
    downloadApiFile(`/reports/cfdi-visor-monthly-excel?${params.toString()}`, `Visor_SAT_Resumen_Mensual_${direccion}_${year}.xlsx`);
  };

  const applyMonth = (ym) => {
    const range = monthRangeFromYm(ym);
    const [yPart, mPart] = String(ym || "").split("-");
    setSelectedMonth(ym);
    setMonthPayments([]);
    if (isSatFixedVisor) {
      if (yPart) setYear(parseInt(yPart, 10));
      if (mPart) setSatMes(mPart);
      load({ satMes: mPart || "all" });
    } else {
      setFechaDesde(range.from);
      setFechaHasta(range.to);
      load({ fechaDesde: range.from, fechaHasta: range.to });
    }
    setYearMode("year");
  };

  const toggleEstado = async (item) => {
    const next = item.estado === "Cancelado" ? "Vigente" : "Cancelado";
    const fd = new FormData(); fd.append("estado", next);
    await api.patch(`/cfdi/${item.id}/estado`, fd, { headers: { "Content-Type": "multipart/form-data" }});
    toast.success(`CFDI marcado como ${next}`);
    load();
  };

  const submitBulkEstado = async () => {
    // Parse UUIDs from textarea: split by whitespace/commas/lines
    const list = bulkUuids.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
    if (!list.length) { toast.error("Pega al menos un UUID"); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/cfdi/bulk-estado", {
        uuids: list, estado: bulkEstado, client_id: clientId || null,
      });
      toast.success(`${data.modified} CFDI actualizados de ${data.uuids_received} UUIDs`);
      setBulkCancelOpen(false);
      setBulkUuids("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al actualizar");
    } finally { setBulkBusy(false); }
  };

  const tableItems = selectedMonth
    ? items.filter((c) => cfdiYmFromRow(c) === selectedMonth)
    : items;
  const invoiceItems = tableItems;
  const paymentItemsForTotals = selectedMonth
    ? monthPayments.filter((c) => cfdiYmFromRow(c) === selectedMonth)
    : [];
  const totalsInvoices = invoiceItems.reduce(
    (acc, c) => ({
      subtotal: acc.subtotal + (c.subtotal || 0),
      iva: acc.iva + (c.iva_trasladado || 0),
      total: acc.total + (c.total || 0),
    }),
    { subtotal: 0, iva: 0, total: 0 }
  );
  const totalsPayments = paymentItemsForTotals.reduce(
    (acc, c) => {
      const m = (c.pagos || []).reduce((s, p) => s + (p.monto || 0), 0);
      return {
        subtotal: acc.subtotal + (c.subtotal || 0),
        iva: acc.iva + (c.iva_trasladado || 0),
        total: acc.total + m,
      };
    },
    { subtotal: 0, iva: 0, total: 0 }
  );
  const totals = useCobradosLayout
    ? {
        ...totalsInvoices,
        cobrado:
          fiscalSummary?.cobrado_periodo?.total ??
          Math.round((totalsInvoices.total + totalsPayments.total) * 100) / 100,
      }
    : { ...totalsInvoices };

  const years = [year+1, year, year-1, year-2, year-3, year-4];
  /** Evita valor inválido en Radix Select (puede colgar la UI si `clientId` no coincide con ningún ítem). */
  const clientSelectValue = clients.length && clients.some((c) => c.id === clientId) ? clientId : undefined;
  const clientRfc = clients.find((c) => c.id === clientId)?.rfc || "";

  const exportPeriodDates = useMemo(() => {
    if (isSatFixedVisor) {
      return satPeriodRange(year, satMes);
    }
    if (fechaDesde && fechaHasta) {
      return { from: fechaDesde, to: fechaHasta };
    }
    if (yearMode === "year" && year) {
      return { from: `${year}-01-01`, to: `${year}-12-31` };
    }
    return { from: "", to: "" };
  }, [isSatFixedVisor, year, satMes, fechaDesde, fechaHasta, yearMode]);

  const showExportToolbar = !isNominaView && (isSatFixedVisor || openSection === "emitido" || openSection === "recibido");

  const headerRight = (
          <div className="flex items-center gap-2">
            <Select value={clientSelectValue} onValueChange={setClientId}>
              <SelectTrigger className="w-56 h-9 rounded-sm" data-testid="visor-client-select">
                <SelectValue placeholder="Cliente" />
              </SelectTrigger>
              <SelectContent>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.razon_social} — {c.rfc}{c.owner_email ? `  ·  ${c.owner_email}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={!isSatFixedVisor && yearMode === "all" ? "all" : String(year)} onValueChange={(v)=>{
              setSelectedMonth("");
              setMonthPayments([]);
              if (!isSatFixedVisor) {
                setFechaDesde("");
                setFechaHasta("");
              }
              if (!isSatFixedVisor && v === "all") setYearMode("all");
              else {
                setYearMode("year");
                setYear(parseInt(v, 10));
              }
            }}>
              <SelectTrigger className="w-32 h-9 rounded-sm" data-testid="visor-year-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {!isSatFixedVisor && <SelectItem value="all">Todos los años</SelectItem>}
                {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        );

  const filterToolbar = (
            <div className="flex items-center gap-2 flex-wrap">
              {isSatFixedVisor ? (
                <>
                  <div className="flex items-center gap-1.5 border border-slate-200 rounded-sm pl-2 pr-1 h-8 bg-white">
                    <CalendarRange className="w-3.5 h-3.5 text-slate-400 shrink-0"/>
                    <span className="text-xs text-slate-600 whitespace-nowrap hidden sm:inline">Mes fiscal</span>
                    <Select value={satMes} onValueChange={(v) => {
                      setSatMes(v);
                      setSelectedMonth("");
                      setMonthPayments([]);
                    }}>
                      <SelectTrigger className="h-7 min-w-[11.5rem] text-xs border-0 shadow-none" data-testid="visor-sat-mes-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todo el año ({year})</SelectItem>
                        {MONTH_NAMES.map((nombre, i) => (
                          <SelectItem key={nombre} value={String(i + 1).padStart(2, "0")}>
                            {nombre} {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <span className="text-[11px] text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1 font-medium">
                    Vigentes (como SAT) · sin cancelados
                  </span>
                  {isNominaView && (
                    <span className="text-[11px] text-slate-600 px-2 py-1 bg-violet-50 border border-violet-200 rounded-sm font-medium">
                      Solo CFDI tipo Nómina (emitidos)
                    </span>
                  )}
                </>
              ) : (
                <>
              {/* Date range filter */}
              <div className="flex items-center gap-1.5 border border-slate-200 rounded-sm pl-2 pr-1 h-8 bg-white">
                <CalendarRange className="w-3.5 h-3.5 text-slate-400"/>
                <Select value={fechaField} onValueChange={setFechaField}>
                  <SelectTrigger className="h-7 border-0 shadow-none w-32 text-xs px-1" data-testid="fecha-field-select">
                    <SelectValue/>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fecha">Fecha emisión</SelectItem>
                    <SelectItem value="fecha_pago" disabled={!useCobradosLayout || ["pue","pue_i","pue_n","pue_e","ppd"].includes(paymentStatus)}>
                      Fecha pago (P)
                    </SelectItem>
                  </SelectContent>
                </Select>
                <input type="date" value={fechaDesde} onChange={(e)=>setFechaDesde(e.target.value)}
                       className="h-7 text-xs border border-slate-200 rounded-sm px-1.5 font-mono" data-testid="fecha-desde-input"/>
                <span className="text-slate-400 text-xs">→</span>
                <input type="date" value={fechaHasta} onChange={(e)=>setFechaHasta(e.target.value)}
                       className="h-7 text-xs border border-slate-200 rounded-sm px-1.5 font-mono" data-testid="fecha-hasta-input"/>
                {(fechaDesde || fechaHasta) && (
                  <button onClick={()=>{setFechaDesde(""); setFechaHasta("");}}
                          className="text-slate-400 hover:text-slate-700 text-xs px-1" title="Limpiar rango" data-testid="clear-range-btn">×</button>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5 text-slate-400"/>
                {!isNominaView ? (
                  <Select value={paymentStatus} onValueChange={(v)=>{
                    if (!useCobradosLayout && (v === "p" || v === "cobrados")) return;
                    setPaymentStatus(v);
                    const isPueLike = v === "pue" || v === "pue_i" || v === "pue_n" || v === "pue_e" || v === "ppd";
                    if (isPueLike && fechaField === "fecha_pago") setFechaField("fecha");
                    if (v === "p" && fechaField === "fecha") setFechaField("fecha_pago");
                  }}>
                    <SelectTrigger className="w-60 h-8 rounded-sm text-xs" data-testid="payment-status-filter">
                      <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Estatus pago: Todos</SelectItem>
                      <SelectItem value="pue">PUE — todos</SelectItem>
                      <SelectItem value="pue_i">PUE Ingresos (I)</SelectItem>
                      <SelectItem value="pue_n">PUE Nóminas (N)</SelectItem>
                      <SelectItem value="pue_e">PUE Notas de Crédito (E)</SelectItem>
                      <SelectItem value="ppd">Solo PPD</SelectItem>
                      {useCobradosLayout && (
                        <>
                          <SelectItem value="p">Solo Compl. de Pago (P)</SelectItem>
                          <SelectItem value="cobrados">Cobrados (Ingresos PUE + Compl. de pago)</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-[11px] text-slate-500 px-2 py-1 bg-violet-50 border border-violet-200 rounded-sm font-medium">
                    Solo CFDI tipo Nómina (emitidos)
                  </span>
                )}
              </div>
              <Button onClick={()=>setExcluirCancelados(v=>!v)} variant={excluirCancelados ? "default" : "outline"}
                className={`rounded-sm h-8 text-xs ${excluirCancelados ? "bg-slate-900 hover:bg-slate-800" : ""}`}
                data-testid="toggle-excluir-cancelados">
                <Ban className="w-3.5 h-3.5 mr-1.5"/>
                {excluirCancelados ? "Ocultando cancelados" : "Mostrar cancelados"}
              </Button>
                </>
              )}
              <Button onClick={downloadExcel} variant="outline" className="rounded-sm h-8 text-xs" data-testid="export-excel-btn">
                <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5"/> Excel
              </Button>
              {useCobradosLayout && (
              <Button onClick={downloadMonthlyExcel} variant="outline" className="rounded-sm h-8 text-xs" data-testid="export-monthly-excel-btn">
                <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5"/> Resumen Excel
              </Button>
              )}
              {showExportToolbar && !(embedMode && forceSatPeriodLayout) ? (
                <ExportToolbar
                  clientId={clientId}
                  clientRfc={clientRfc}
                  fechaInicio={exportPeriodDates.from}
                  fechaFin={exportPeriodDates.to}
                  direccion={direccion}
                  allowAmbosToggle={!isSatFixedVisor}
                  defaultIncluirAmbos={
                    openSection === "emitido" ||
                    openSection === "recibido"
                  }
                  showAmbosFlujos={isSatFixedVisor}
                  exportAlignVisor={isSatFixedVisor}
                  exportTipo={isSatFixedVisor ? activeTipo : null}
                  disabled={!clientId || loading}
                  compact
                />
              ) : null}
              <Button onClick={clearAll} variant="outline" className="rounded-sm h-8 text-xs text-red-600 hover:bg-red-50" data-testid="clear-cfdi-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1.5"/> Limpiar
              </Button>
          </div>
  );

  const visorMainBelowToolbar = (
    <>
      {/* Active filters banner */}
      {((isSatFixedVisor && (satMes !== "all" || selectedMonth)) || (!isSatFixedVisor && (fechaDesde || fechaHasta || paymentStatus !== "all"))) && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-xs flex items-center gap-2 flex-wrap" data-testid="active-filters-banner">
          <span className="font-semibold text-amber-900">Filtros activos:</span>
          {isSatFixedVisor && (
            <>
              <span className="bg-white border border-amber-300 px-2 py-0.5 rounded-sm">
                Año {year} · {satMes === "all" ? "Todo el año (por fecha de emisión)" : `${monthLabelFromYm(`${year}-${satMes}`)} (emisión)`}
              </span>
              {selectedMonth && (
                <span className="bg-white border border-amber-300 px-2 py-0.5 rounded-sm">
                  Detalle tabla: {monthLabelFromYm(selectedMonth)}
                </span>
              )}
            </>
          )}
          {!isSatFixedVisor && (fechaDesde || fechaHasta) && (
            <span className="bg-white border border-amber-300 px-2 py-0.5 rounded-sm">
              {fechaField === "fecha_pago" ? "Fecha pago" : "Fecha emisión"} {fechaDesde || "…"} → {fechaHasta || "…"}
            </span>
          )}
          <span className="bg-white border border-amber-300 px-2 py-0.5 rounded-sm">
            {isSatFixedVisor
              ? (direccion === "recibido" ? "Recibidos · facturas I; pagos en tabla separada" : "Emitidos · facturas I; pagos en tabla separada")
              : (isNominaView
                ? "Nómina (solo tipo N emitidos)"
                : (direccion === "recibido" ? "Recibidos (solo facturas I)" : "Emitidos (solo facturas I)"))}
          </span>
          {!isSatFixedVisor && paymentStatus !== "all" && <span className="bg-white border border-amber-300 px-2 py-0.5 rounded-sm">Estatus pago: {paymentStatus}</span>}
          <button
            type="button"
            onClick={() => {
              if (isSatFixedVisor) {
                setSatMes("all");
                setSelectedMonth("");
                setMonthPayments([]);
              } else {
                setFechaDesde("");
                setFechaHasta("");
                setPaymentStatus("all");
                setSelectedMonth("");
              }
            }}
            className="ml-auto text-amber-900 hover:underline"
            data-testid="clear-all-filters-btn"
          >
            Limpiar filtros
          </button>
        </div>
      )}

      {/* Panel de salud de CFDIs: detecta XMLs que necesitan re-upload */}
      {health && health.needs_reupload && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-md p-4" data-testid="cfdi-health-panel">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-amber-900 text-sm">
                {health.total_issues} CFDIs del {year} necesitan re-subirse para datos completos
              </div>
              <div className="text-xs text-amber-800 mt-1">
                {health.by_type.nomina_sin_complemento > 0 && (
                  <>• <strong>{health.by_type.nomina_sin_complemento} Nóminas</strong> sin Complemento 1.2 (ISR y percepciones en 0).{" "}</>
                )}
                {health.by_type.pago_sin_impuestos > 0 && (
                  <>• <strong>{health.by_type.pago_sin_impuestos} Complementos de Pago</strong> sin impuestos (subtotal/IVA en 0).{" "}</>
                )}
                {health.by_type.nc_sin_relacionados > 0 && (
                  <>• <strong>{health.by_type.nc_sin_relacionados} Notas de Crédito</strong> sin vincular a la factura original.{" "}</>
                )}
              </div>
              <div className="mt-3 grid grid-cols-3 md:grid-cols-6 lg:grid-cols-12 gap-1.5">
                {(health.resumen_mes || []).map((m) => {
                  const mesLabel = m.mes.split("-")[1];
                  const mesName = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"][parseInt(mesLabel,10)-1];
                  return (
                    <div key={m.mes} className="bg-white border border-amber-200 rounded-sm px-2 py-1.5 text-center"
                         data-testid={`health-mes-${m.mes}`}
                         title={`Nómina: ${m.nomina_sin_complemento} · Pagos: ${m.pago_sin_impuestos} · NC: ${m.nc_sin_relacionados}`}>
                      <div className="text-[10px] text-slate-500">{mesName}</div>
                      <div className="text-xs font-semibold text-amber-700">{m.issues}</div>
                      <div className="text-[9px] text-slate-400">de {m.total_cfdis}</div>
                    </div>
                  );
                })}
              </div>
              <div className="text-[11px] text-amber-700 mt-2 italic">
                {health.recommendation} Usa "Cargar carpeta" del mes afectado — el sistema actualiza sin duplicar.
              </div>
            </div>
          </div>
        </div>
      )}
      {health && !health.needs_reupload && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2.5 text-xs text-emerald-800 flex items-center gap-2" data-testid="cfdi-health-ok">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          Todos los CFDIs del {year} tienen datos completos.
        </div>
      )}

      {/* Fiscal summary */}
      {fiscalSummary && (
        <div
          className={`grid gap-3 ${useCobradosLayout ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-6" : "grid-cols-2 md:grid-cols-4"}`}
          data-testid="fiscal-summary"
        >
          {useCobradosLayout ? (
            <>
              <SummaryBox label="Ingresos PUE en periodo" count={fiscalSummary.cobrado_periodo?.pue_count ?? fiscalSummary.pue.count}
                          amount={fiscalSummary.cobrado_periodo?.pue_total ?? fiscalSummary.pue.total} color="emerald"
                          hint="Tipo I, por fecha emisión"/>
              <SummaryBox label="Complementos en periodo" count={fiscalSummary.cobrado_periodo?.p_count ?? fiscalSummary.complementos.count}
                          amount={fiscalSummary.cobrado_periodo?.p_total ?? fiscalSummary.complementos.total_cobrado} color="blue"
                          hint="Por fecha de pago · confronta Excel"/>
              <SummaryBox label="Total Cobrado periodo" count={(fiscalSummary.cobrado_periodo?.pue_count || 0) + (fiscalSummary.cobrado_periodo?.p_count || 0)}
                          amount={fiscalSummary.cobrado_periodo?.total ?? 0} color="emerald"
                          hint="PUE + complementos (Similar SAT)"/>
            </>
          ) : (
            <SummaryBox label="Facturas PUE" count={fiscalSummary.pue.count} amount={fiscalSummary.pue.total} color="emerald"
                        hint="Por fecha de emisión · sin complementos de pago"/>
          )}
          <SummaryBox label="PPD pendientes" count={fiscalSummary.ppd_pendiente.count} amount={fiscalSummary.ppd_pendiente.total} color="amber"/>
          <SummaryBox label="PPD cobradas" count={fiscalSummary.ppd_cobrado.count} amount={fiscalSummary.ppd_cobrado.total} color="slate"/>
          <SummaryBox
            label="Total CFDI listados"
            count={Object.entries(fiscalSummary.by_tipo || {})
              .filter(([t]) => t !== "P")
              .reduce((a, [, b]) => a + (b.count || 0), 0)}
            amount={Object.entries(fiscalSummary.by_tipo || {})
              .filter(([t]) => t !== "P")
              .reduce((a, [, b]) => a + (b.total || 0), 0)}
            color="slate"
          />
        </div>
      )}

      {!useCobradosLayout && yearMode === "year" && !monthlySummary && !monthlyLoading && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-xs text-slate-600" data-testid="visor-xml-hint">
          Listado por <strong>fecha de emisión</strong> (facturas y notas de crédito). Complementos de pago y cruce con Excel:{" "}
          <strong>Visores CFDI (Similar SAT)</strong> y <strong>Confronta</strong>.
        </div>
      )}

      {yearMode === "year" && (
        <div
          className="bg-white border border-slate-200 rounded-sm overflow-hidden"
          data-testid={useCobradosLayout ? "monthly-summary" : "monthly-summary-xml"}
        >
          <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between gap-2">
            <div>
              <div className="text-xs font-semibold text-slate-900">Resumen por mes</div>
              <div className="text-[11px] text-slate-500">
                {useCobradosLayout
                  ? (monthlySummary?.mode === "nomina"
                    ? "Nómina (sin IVA): sueldos = percepciones del complemento, ISR retenido, complementos de pago = montos pagados."
                    : "PUE por emisión y complementos de pago por fecha de pago. Clic en un mes para filtrar.")
                  : (monthlySummary?.mode === "xml_nomina"
                    ? "Nóminas emitidas por mes (fecha de emisión). Clic en un mes para ver el listado."
                    : "Comprobantes emitidos por mes (I + NC, fecha de emisión). Clic en un mes para ver el listado.")}
              </div>
            </div>
            {selectedMonth && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => {
                  setSelectedMonth("");
                  setMonthPayments([]);
                  if (isSatFixedVisor) {
                    setSatMes("all");
                    load({ satMes: "all" });
                  } else {
                    setFechaDesde("");
                    setFechaHasta("");
                    load({ fechaDesde: "", fechaHasta: "" });
                  }
                }}
                data-testid="clear-month-btn"
              >
                Quitar mes
              </Button>
            )}
          </div>
          <div className="overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <Th>Mes</Th>
                  {useCobradosLayout ? (
                    <>
                      <Th right>CFDI PUE</Th>
                      {monthlySummary?.mode === "nomina" ? (
                        <>
                          <Th right>Sueldos PUE</Th>
                          <Th right>ISR ret. PUE</Th>
                          <Th right>Total PUE</Th>
                          <Th right>CFDI Pago</Th>
                          <Th right>Monto Pago</Th>
                          <Th right>ISR ret. Pago</Th>
                          <Th right>Total Pago</Th>
                        </>
                      ) : (
                        <>
                          <Th right>Subtotal PUE</Th>
                          <Th right>IVA PUE</Th>
                          <Th right>Total PUE</Th>
                          <Th right>CFDI Pago</Th>
                          <Th right>Subtotal Pago</Th>
                          <Th right>IVA Pago</Th>
                          <Th right>Total Pago</Th>
                        </>
                      )}
                      <Th right>Total CFDI</Th>
                    </>
                  ) : (
                    <>
                      <Th right>CFDI</Th>
                      {monthlySummary?.mode === "xml_nomina" ? (
                        <>
                          <Th right>Sueldos</Th>
                          <Th right>ISR ret.</Th>
                        </>
                      ) : (
                        <>
                          <Th right>Subtotal</Th>
                          <Th right>IVA</Th>
                        </>
                      )}
                      <Th right>Total</Th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {monthlyLoading && (
                  <tr>
                    <td colSpan={useCobradosLayout ? 10 : 5} className="px-4 py-8 text-center text-slate-500">
                      <Loader2 className="w-4 h-4 inline animate-spin mr-2"/>Cargando resumen...
                    </td>
                  </tr>
                )}
                {!monthlyLoading && useCobradosLayout && monthlySummary?.rows?.map((row) => {
                  const active = selectedMonth === row.mes;
                  return (
                    <tr
                      key={row.mes}
                      className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${active ? "bg-amber-50" : ""}`}
                      onClick={() => applyMonth(row.mes)}
                      data-testid={`monthly-row-${row.mes}`}
                    >
                      <td className="px-3 py-2 font-semibold text-slate-900 whitespace-nowrap">{row.label}</td>
                      <td className="px-3 py-2 text-right num">{fmtNumber(row.pue.count)}</td>
                      <td className="px-3 py-2 text-right num">{fmtMoney(row.pue.subtotal)}</td>
                      <td className="px-3 py-2 text-right num">{fmtMoney(row.pue.iva)}</td>
                      <td className="px-3 py-2 text-right num font-semibold text-emerald-700">{fmtMoney(row.pue.total)}</td>
                      <td className="px-3 py-2 text-right num">{fmtNumber(row.pago.count)}</td>
                      <td className="px-3 py-2 text-right num">{fmtMoney(row.pago.subtotal)}</td>
                      <td className="px-3 py-2 text-right num">{fmtMoney(row.pago.iva)}</td>
                      <td className="px-3 py-2 text-right num font-semibold text-blue-700">{fmtMoney(row.pago.total)}</td>
                      <td className="px-3 py-2 text-right num font-semibold">{fmtNumber(row.all.count)}</td>
                    </tr>
                  );
                })}
                {!monthlyLoading && !useCobradosLayout && monthlySummary?.rows?.map((row) => {
                  const active = selectedMonth === row.mes;
                  return (
                    <tr
                      key={row.mes}
                      className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${active ? "bg-amber-50" : ""}`}
                      onClick={() => applyMonth(row.mes)}
                      data-testid={`monthly-row-${row.mes}`}
                    >
                      <td className="px-3 py-2 font-semibold text-slate-900 whitespace-nowrap">{row.label}</td>
                      <td className="px-3 py-2 text-right num">{fmtNumber(row.count)}</td>
                      <td className="px-3 py-2 text-right num">{fmtMoney(row.subtotal)}</td>
                      <td className="px-3 py-2 text-right num">{fmtMoney(row.iva)}</td>
                      <td className="px-3 py-2 text-right num font-semibold text-emerald-700">{fmtMoney(row.total)}</td>
                    </tr>
                  );
                })}
              </tbody>
              {!monthlyLoading && monthlySummary?.totales && useCobradosLayout && (
                <tfoot className="bg-slate-50">
                  <tr className="border-t border-slate-200">
                    <td className="px-3 py-2 font-semibold">Totales</td>
                    <td className="px-3 py-2 text-right num font-semibold">{fmtNumber(monthlySummary.totales.pue.count)}</td>
                    <td className="px-3 py-2 text-right num font-semibold">{fmtMoney(monthlySummary.totales.pue.subtotal)}</td>
                    <td className="px-3 py-2 text-right num font-semibold">{fmtMoney(monthlySummary.totales.pue.iva)}</td>
                    <td className="px-3 py-2 text-right num font-semibold text-emerald-700">{fmtMoney(monthlySummary.totales.pue.total)}</td>
                    <td className="px-3 py-2 text-right num font-semibold">{fmtNumber(monthlySummary.totales.pago.count)}</td>
                    <td className="px-3 py-2 text-right num font-semibold">{fmtMoney(monthlySummary.totales.pago.subtotal)}</td>
                    <td className="px-3 py-2 text-right num font-semibold">{fmtMoney(monthlySummary.totales.pago.iva)}</td>
                    <td className="px-3 py-2 text-right num font-semibold text-blue-700">{fmtMoney(monthlySummary.totales.pago.total)}</td>
                    <td className="px-3 py-2 text-right num font-semibold">{fmtNumber(monthlySummary.totales.all.count)}</td>
                  </tr>
                </tfoot>
              )}
              {!monthlyLoading && monthlySummary?.totales && !useCobradosLayout && (
                <tfoot className="bg-slate-50">
                  <tr className="border-t border-slate-200">
                    <td className="px-3 py-2 font-semibold">Totales</td>
                    <td className="px-3 py-2 text-right num font-semibold">{fmtNumber(monthlySummary.totales.count)}</td>
                    <td className="px-3 py-2 text-right num font-semibold">{fmtMoney(monthlySummary.totales.subtotal)}</td>
                    <td className="px-3 py-2 text-right num font-semibold">{fmtMoney(monthlySummary.totales.iva)}</td>
                    <td className="px-3 py-2 text-right num font-semibold text-emerald-700">{fmtMoney(monthlySummary.totales.total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {direccion === "recibido" && !isNominaView && (
        <div
          className="bg-amber-50/90 border border-amber-200 rounded-sm px-3 py-2 text-xs text-amber-950 leading-relaxed"
          role="note"
          data-testid="visor-recibidos-tipo-i-notice"
        >
          En <strong>Egresos / Recibidos</strong> las facturas de gasto suelen ser tipo SAT <strong>I</strong> (el SAT las
          llama «Ingreso» del proveedor). Aquí se muestran como <strong>Factura recibida</strong> solo si su RFC aparece en{" "}
          <strong>RFC Receptor</strong>. Las ventas que usted emitió no deben listarse en esta pestaña.
        </div>
      )}

      {yearMode !== "year" && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-xs text-slate-600">
          {useCobradosLayout
            ? (activeTipo === "N"
              ? "Selecciona un año para ver el resumen mensual de nóminas (sueldos, ISR y complementos de pago)."
              : "Selecciona un año para ver el resumen mensual de CFDI PUE y Complementos de Pago.")
            : (activeTipo === "N"
              ? "Selecciona un año para ver el resumen mensual de nóminas emitidas."
              : "Selecciona un año para ver el resumen mensual de comprobantes por fecha de emisión.")}
        </div>
      )}


      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="cfdi-table">
        <div className="overflow-auto max-h-[calc(100vh-360px)]">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <Th>Fecha emisión</Th>
                <Th>Fecha pago</Th>
                <Th>UUID</Th>
                <Th>Serie/Folio</Th>
                <Th>Tipo</Th>
                <Th>Mét. pago</Th>
                <Th>Estado</Th>
                <Th>Compl. pago</Th>
                <Th>Facturas relacionadas</Th>
                <Th>RFC Emisor</Th>
                <Th>RFC Receptor</Th>
                <Th right>Subtotal</Th>
                <Th right>IVA</Th>
                <Th right>Total</Th>
                <Th>Acc.</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={15} className="px-4 py-8 text-center text-slate-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2"/>Cargando...</td></tr>}
              {!loading && tableItems.length === 0 && (
                <tr><td colSpan={15} className="px-4 py-12 text-center">
                  <FileX2 className="w-8 h-8 mx-auto text-slate-300 mb-2"/>
                  <div className="text-slate-500">Sin CFDI cargados para este filtro.</div>
                  <div className="text-[11px] text-slate-400 mt-1">Carga archivos individuales o una carpeta completa (incluye subcarpetas).</div>
                </td></tr>
              )}
              {!loading && tableItems.map(c => {
                const folioStr = [c.serie, c.folio].filter(Boolean).join("-") || "—";
                const isP = c.tipo_comprobante === "P";
                const isCancel = c.estado === "Cancelado";
                // Compute display values for fecha de pago and facturas relacionadas
                let fechaPagoDisplay = "—";
                let relacionadasDisplay = "—";
                let relacionadasTitle = "";
                if (isP) {
                  fechaPagoDisplay = (c.fecha_pago || "").slice(0, 10) || "—";
                  const rel = c.facturas_relacionadas || [];
                  if (rel.length === 1) {
                    relacionadasDisplay = rel[0].serie_folio;
                    relacionadasTitle = `${rel[0].serie_folio} · UUID ${rel[0].uuid} · Pagado ${fmtMoney(rel[0].imp_pagado)}`;
                  } else if (rel.length > 1) {
                    relacionadasDisplay = `${rel[0].serie_folio} +${rel.length - 1}`;
                    relacionadasTitle = rel.map(r => `${r.serie_folio} · ${fmtMoney(r.imp_pagado)}`).join("\n");
                  }
                } else if (c.tiene_complemento_pago && c.pagos_recibidos?.length) {
                  const pr = c.pagos_recibidos;
                  fechaPagoDisplay = (pr[pr.length - 1].fecha_pago || "").slice(0, 10) || "—";
                  relacionadasDisplay = `Pagada por ${pr.length} compl.`;
                  relacionadasTitle = pr.map(p => `Pago ${(p.pago_uuid||"").slice(0,8)}… · ${(p.fecha_pago||"").slice(0,10)} · ${fmtMoney(p.imp_pagado)}`).join("\n");
                }
                return (
                <tr key={c.id} className={`border-t border-slate-100 row-hover ${isCancel ? "opacity-60" : ""} ${focusRowUuid && (c.uuid || "").toUpperCase() === focusRowUuid ? "ring-2 ring-inset ring-[#0F2D5C]/35 bg-sky-50/60" : ""}`} data-testid={`cfdi-row-${c.uuid?.slice(0,8)}`}>
                  <td className="px-3 py-2 font-mono text-slate-700 whitespace-nowrap">{(c.fecha || "").slice(0,10)}</td>
                  <td className={`px-3 py-2 font-mono whitespace-nowrap ${isP ? "text-blue-700 font-semibold" : "text-slate-500"}`}>{fechaPagoDisplay}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-700">{c.uuid}</td>
                  <td className={`px-3 py-2 font-mono text-slate-600 ${isCancel ? "line-through" : ""}`}>{folioStr}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide border ${
                        c.tipo_comprobante === "I" && direccion === "recibido"
                          ? "bg-amber-50 text-amber-900 border-amber-200"
                          : tipoColor(c.tipo_comprobante)
                      }`}
                      title={
                        c.tipo_comprobante === "I" && direccion === "recibido"
                          ? "Tipo SAT I (Ingreso): factura de gasto que usted recibió; no es una venta emitida"
                          : undefined
                      }
                    >
                      {visorCfdiTipoLabel(c.tipo_comprobante, direccion)}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded-sm border ${
                      c.metodo_pago === "PUE" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                      c.metodo_pago === "PPD" ? "bg-blue-50 text-blue-700 border-blue-200" :
                      "bg-slate-50 text-slate-500 border-slate-200"
                    }`}>{formatPagoLabel(c.metodo_pago)}</span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide border ${
                      isCancel ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"
                    }`}>{c.estado || "Vigente"}</span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {isP ? (
                      <span title="Este es un complemento de pago" className="text-blue-700">
                        <Link2 className="w-3.5 h-3.5 inline"/>
                      </span>
                    ) : c.tiene_complemento_pago ? (
                      <span title="Pagado · cuenta con complemento" className="text-emerald-700">
                        <CircleCheckBig className="w-3.5 h-3.5 inline"/>
                      </span>
                    ) : c.metodo_pago === "PPD" ? (
                      <span title="PPD pendiente de pago" className="text-amber-700">
                        <CircleX className="w-3.5 h-3.5 inline"/>
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px]" title={relacionadasTitle}>{relacionadasDisplay}</td>
                  <td className="px-3 py-2 font-mono text-slate-700">{c.rfc_emisor}</td>
                  <td className="px-3 py-2 font-mono text-slate-700">{c.rfc_receptor}</td>
                  <td className="px-3 py-2 text-right num text-slate-800">{fmtMoney(c.subtotal)}</td>
                  <td className="px-3 py-2 text-right num text-slate-800">{fmtMoney(c.iva_trasladado)}</td>
                  <td className="px-3 py-2 text-right num font-semibold text-slate-900">
                    {c.tipo_comprobante === "P"
                      ? <span title="Monto del complemento de pago" className="text-blue-700">
                          {fmtMoney((c.pagos || []).reduce((s,p)=>s+(p.monto||0),0))}
                        </span>
                      : fmtMoney(c.total)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={(e) => { e.stopPropagation(); setDetail(c); }} 
                        className="h-7 px-2 text-[10px] font-bold uppercase tracking-wider border-[#0F2D5C] text-[#0F2D5C] hover:bg-[#0F2D5C] hover:text-white transition-colors"
                        data-testid={`detail-${c.uuid?.slice(0,8)}`}
                      >
                        Vista
                      </Button>
                      <button onClick={(e) => { e.stopPropagation(); openCfdiPdfInNewTab(c.id || c.uuid); }} title="Ver PDF" className="p-1 text-slate-500 hover:text-blue-700" data-testid={`pdf-${c.uuid?.slice(0,8)}`}>
                        <FileSearch className="w-4 h-4"/>
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); toggleEstado(c); }} title={isCancel ? "Marcar como vigente" : "Marcar como cancelado"} className="p-1 text-slate-500 hover:text-red-700" data-testid={`toggle-estado-${c.uuid?.slice(0,8)}`}>
                        {isCancel ? <CircleCheckBig className="w-4 h-4"/> : <CircleX className="w-4 h-4"/>}
                      </button>
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
            {!loading && tableItems.length > 0 && (
              <tfoot className="bg-slate-50 sticky bottom-0">
                <tr className="border-t-2 border-slate-300">
                  <td colSpan={11} className="px-3 py-2 text-right text-[10px] uppercase tracking-wider font-semibold text-slate-600">
                    Totales · {fmtNumber(tableItems.length)} CFDI
                    {useCobradosLayout ? (
                      <span className="ml-2 normal-case font-normal text-slate-500">
                        (Tabla: solo CFDI {isNominaView ? "Nómina N" : "factura I"}; Compl. pago del periodo: {fmtMoney(totalsPayments.total)}
                        {totals.cobrado != null ? ` · Cobrado periodo: ${fmtMoney(totals.cobrado)}` : ""})
                      </span>
                    ) : (
                      <span className="ml-2 normal-case font-normal text-slate-500">
                        (Solo facturas por fecha de emisión, sin complementos de pago)
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right num font-bold">{fmtMoney(totalsInvoices.subtotal)}</td>
                  <td className="px-3 py-2 text-right num font-bold">{fmtMoney(totalsInvoices.iva)}</td>
                  <td className="px-3 py-2 text-right num font-bold text-emerald-700">
                    {fmtMoney(totals.total)}
                  </td>
                  <td/>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      {useCobradosLayout && selectedMonth && paymentItemsForTotals.length > 0 && (
        <div className="mt-4 bg-white border border-blue-100 rounded-sm overflow-hidden" data-testid="sat-payment-period-table">
          <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 text-xs font-semibold text-blue-900">
            Complementos de pago {direccion === "recibido" ? "recibidos" : "emitidos"} · {monthLabelFromYm(selectedMonth)}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                <tr>
                  <Th>Fecha pago</Th>
                  <Th>UUID pago</Th>
                  <Th>Facturas relacionadas</Th>
                  <Th right>Monto pagado</Th>
                  <Th>Acc.</Th>
                </tr>
              </thead>
              <tbody>
                {paymentItemsForTotals.map((p) => {
                  const rel = p.facturas_relacionadas || [];
                  const monto = (p.pagos || []).reduce((s, pg) => s + (pg.monto || 0), 0);
                  return (
                    <tr key={p.id || p.uuid} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono text-slate-700">{(p.fecha_pago || p.fecha || "").slice(0, 10) || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[10px] text-blue-700">{p.uuid}</td>
                      <td className="px-3 py-2 text-slate-600">
                        {rel.length ? rel.map((r) => r.serie_folio || (r.uuid || "").slice(0, 8)).join(", ") : "—"}
                      </td>
                      <td className="px-3 py-2 text-right num font-semibold text-blue-700">{fmtMoney(monto)}</td>
                      <td className="px-3 py-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); setDetail(p); }}
                          className="h-7 px-2 text-[10px] font-bold uppercase tracking-wider border-blue-700 text-blue-700 hover:bg-blue-700 hover:text-white transition-colors"
                          data-testid={`detail-payment-${p.uuid?.slice(0,8)}`}
                        >
                          Vista
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );

  const renderReglasBody = () => (
    <div className="px-4 pb-5 pt-4 space-y-3 text-sm text-slate-600 leading-relaxed max-w-4xl">
      <p>
        El listado respeta el comprobante y la fecha del filtro (emisión o pago para complementos). Las{" "}
        <strong>notas de crédito</strong> aplican sobre facturas relacionadas; si el XML no trae la relación,
        el vínculo puede verse incompleto hasta re-cargar o re-parsear el XML.
      </p>
      <p>
        Un <strong>complemento de pago</strong> puede liquidar varias facturas en un solo CFDI tipo P; el importe
        mostrado es el del pago, no necesariamente el total de cada factura individual.
      </p>
      <p>
        Facturas <strong>PPD</strong> aparecen como pendientes hasta registrarse pagos; lo &quot;cobrado&quot; en confronta
        sigue la lógica PUE + complementos en el periodo.
      </p>
    </div>
  );

  const renderReglasSection = () => {
    const expanded = openSection === "reglas";
    return (
      <section
        className="bg-white relative z-0"
        data-testid="visor-section-reglas"
        aria-label="Reglas y límites (NC, sustitución, pagos parciales)"
      >
        <div className={satExpandRowCn()}>
          <div
            className={satExpandMainCn(expanded)}
            data-testid="visor-reglas-heading"
          >
            <Scale className="w-4 h-4 text-slate-600 shrink-0" aria-hidden />
            <span className="truncate">Reglas y límites (NC, sustitución, pagos parciales)</span>
          </div>
          <button
            type="button"
            className={satExpandToggleCn(expanded)}
            aria-expanded={expanded}
            aria-label={expanded ? "Contraer reglas y límites" : "Expandir reglas y límites"}
            data-testid="visor-reglas-toggle"
            onClick={() => {
              if (expanded) setOpenSection(null);
              else setOpenSection("reglas");
            }}
          >
            <ChevronDown className={satExpandChevronCn(expanded)} aria-hidden />
          </button>
        </div>
        {expanded && (
          <div className="relative z-0 border-t border-slate-100">
            {renderReglasBody()}
          </div>
        )}
      </section>
    );
  };

  return (
    <>
      {embedMode ? (
        <div className="flex flex-wrap items-center justify-end gap-2 mb-4">{headerRight}</div>
      ) : (
      <PageHeader
        title="Visor de XML SAT"
        subtitle="Catálogo por fecha de emisión (emitidos, recibidos, nómina). Complementos de pago y confronta con Excel: Similar SAT y Confronta."
        breadcrumb={[VISOR_MODULE_BREADCRUMB_ROOT, "Visor de XML SAT"]}
        right={headerRight}
      />
      )}
      <div className={embedMode ? "space-y-4" : "p-8 space-y-4"}>
        {!clients.length ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-sm p-10 text-center">
            <FileX2 className="w-8 h-8 mx-auto text-slate-400 mb-2"/>
            <div className="text-sm text-slate-500">Primero registra un cliente.</div>
          </div>
        ) : (
        <>
          {syncMeta && (
            <div
              className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-[11px] text-slate-700 leading-snug"
              data-testid="visor-sync-meta"
            >
              <span className="font-semibold text-slate-800">Copia local de XML: </span>
              última integración <span className="font-mono">{fmtSyncAt(syncMeta.last_xml_ingested_at)}</span>
              {" · "}
              <span className="font-mono">{fmtNumber(syncMeta.cfdi_count)}</span> CFDI guardados.
              <div className="text-slate-500 mt-1">{syncMeta.disclaimer}</div>
            </div>
          )}
          {fixedView ? (
            <>
              <div className={embedMode ? "mb-3" : "max-w-7xl mx-auto mb-3"}>
                <div className="rounded-sm border border-slate-200 bg-white shadow-sm overflow-hidden">
                  {renderReglasSection()}
                </div>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-white border border-slate-200 rounded-sm px-3 py-2">
                {fixedView === "emitido" ? (
                  <div
                    className="flex items-center gap-2 px-3 py-2 text-[11px] font-semibold text-slate-800 border border-slate-200 rounded-sm bg-slate-50/80 shrink-0"
                    data-testid="tab-emitido"
                  >
                    <ChevronRight className="w-3.5 h-3.5 shrink-0 text-slate-500" aria-hidden />
                    {sectionLabel("emitido")}
                  </div>
                ) : fixedView === "recibido" ? (
                  <div
                    className="flex items-center gap-2 px-3 py-2 text-[11px] font-semibold text-slate-800 border border-slate-200 rounded-sm bg-slate-50/80 shrink-0"
                    data-testid="tab-recibido"
                  >
                    <ChevronRight className="w-3.5 h-3.5 shrink-0 text-slate-500" aria-hidden />
                    {sectionLabel("recibido")}
                  </div>
                ) : (
                  <div
                    className="flex items-center gap-2 px-3 py-2 text-[11px] font-semibold text-slate-800 border border-slate-200 rounded-sm bg-slate-50/80 shrink-0"
                    data-testid="tab-nomina"
                  >
                    <ChevronRight className="w-3.5 h-3.5 shrink-0 text-slate-500" aria-hidden />
                    {sectionLabel("nomina")}
                  </div>
                )}
                {filterToolbar}
              </div>
              {visorMainBelowToolbar}
            </>
          ) : (
            <div className={embedMode ? "" : "max-w-7xl mx-auto"}>
              {!openSection ? (
                <div className="space-y-4" data-testid="visor-section-cards">
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Elija una función del visor. Solo se cargará la sección que abra (misma lógica que el acordeón anterior).
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {VISOR_HUB_SECTIONS.map(({ id, label, description, Icon, testid }) => (
                      <ModuleFeatureCard
                        key={id}
                        label={label}
                        description={description}
                        icon={Icon}
                        hasModuleAccess
                        accessLoading={false}
                        testId={testid}
                        onEnter={() => openVisorSection(id)}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-sm h-9 text-xs font-semibold"
                      data-testid="visor-hub-back"
                      onClick={() => setOpenSection(null)}
                    >
                      <ArrowLeft className="w-3.5 h-3.5 mr-1.5" aria-hidden />
                      Volver al menú del visor
                    </Button>
                    {activeHubSection ? (
                      <h2 className="text-sm font-bold text-[#0F2D5C]">{activeHubSection.label}</h2>
                    ) : null}
                  </div>
                  <div className="rounded-sm border border-slate-200 bg-white shadow-sm overflow-hidden">
                    {openSection === "reglas" && (
                      <section data-testid="visor-section-reglas" aria-label="Reglas y límites">
                        {renderReglasBody()}
                      </section>
                    )}
                    {VISOR_MAIN_TABS.map(({ value: v, testid }) =>
                      openSection === v ? (
                        <section
                          key={v}
                          data-testid={`visor-section-${v}`}
                          aria-label={sectionLabel(v)}
                          className="px-4 pb-5 pt-4 space-y-4 overflow-x-auto"
                        >
                          <span data-testid={testid} className="sr-only">
                            {sectionLabel(v)}
                          </span>
                          <div className="bg-white border border-slate-200 rounded-sm px-3 py-2">{filterToolbar}</div>
                          <div data-testid={`visor-panel-${v}`}>{visorMainBelowToolbar}</div>
                        </section>
                      ) : null
                    )}
                    {openSection === OPEN_SECTION_SIMILAR_SAT && (
                      <section
                        data-testid="visor-section-similar-sat"
                        aria-label="Visores CFDI (Similar SAT)"
                        className="px-2 sm:px-4 pb-5 pt-4 overflow-x-auto"
                      >
                        <div data-testid="visor-panel-similar-sat">
                          <Suspense
                            fallback={
                              <div className="flex justify-center py-10">
                                <Loader2 className="h-8 w-8 animate-spin text-slate-400" aria-hidden />
                              </div>
                            }
                          >
                            <VisoresSatHubBody wrapperClassName="space-y-4 max-w-none" />
                          </Suspense>
                        </div>
                      </section>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
        )}
      </div>

      <CfdiDetailDialog detail={detail} onClose={closeDetail} />

      <Dialog open={bulkCancelOpen} onOpenChange={setBulkCancelOpen}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="bulk-cancel-dialog">
          <DialogHeader>
            <DialogTitle style={{fontFamily:"'Chivo'"}}>Marcar CFDI por lote</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Pega los UUIDs cancelados (uno por línea, separados por coma o espacios). Puedes copiarlos del visor SAT.
              Solo se actualizarán los CFDI del cliente seleccionado.
            </p>
            <div className="flex gap-2 items-center">
              <label className="text-xs text-slate-700">Estado a aplicar:</label>
              <Select value={bulkEstado} onValueChange={setBulkEstado}>
                <SelectTrigger className="w-40 h-8 rounded-sm text-xs" data-testid="bulk-estado-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cancelado">Cancelado</SelectItem>
                  <SelectItem value="Vigente">Vigente (revertir)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Textarea
              data-testid="bulk-uuids-textarea"
              value={bulkUuids}
              onChange={(e)=>setBulkUuids(e.target.value)}
              placeholder="A1B2C3D4-E5F6-7890-ABCD-EF1234567890&#10;B2C3D4E5-F6A7-8901-BCDE-F23456789012&#10;..."
              rows={10}
              className="rounded-sm font-mono text-xs"
              spellCheck={false}
            />
            <p className="text-[11px] text-slate-500">
              {bulkUuids.split(/[\s,;]+/).filter(Boolean).length} UUID(s) detectados.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setBulkCancelOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={submitBulkEstado} disabled={bulkBusy} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="bulk-submit-btn">
              {bulkBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin"/>}
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Th({ children, right }) {
  return (
    <th className={`px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold ${right ? "text-right" : "text-left"}`}>
      {children}
    </th>
  );
}

function SummaryBox({ label, count, amount, color = "slate", hint }) {
  const colors = {
    emerald: "border-emerald-200 bg-emerald-50",
    amber:   "border-amber-200 bg-amber-50",
    blue:    "border-blue-200 bg-blue-50",
    red:     "border-red-200 bg-red-50",
    slate:   "border-slate-200 bg-white",
  };
  return (
    <div className={`border ${colors[color]} rounded-sm px-3 py-2.5`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">{label}</div>
        <div className="text-[10px] num text-slate-500">{fmtNumber(count)}</div>
      </div>
      <div className="num text-base font-bold mt-1 text-slate-900">{fmtMoney(amount)}</div>
      {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function CfdiDetailDialog({ detail, onClose }) {
  const [pdfBlobUrl, setPdfBlobUrl] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const pdfObjectUrlRef = useRef(null);
  const cfdiRef = detail?.id || detail?.uuid || "";

  useEffect(() => {
    let cancelled = false;
    const cfdiId = cfdiRef;

    if (!cfdiId) {
      if (pdfObjectUrlRef.current) {
        URL.revokeObjectURL(pdfObjectUrlRef.current);
        pdfObjectUrlRef.current = null;
      }
      setPdfBlobUrl("");
      setPdfError("");
      setPdfBusy(false);
      return;
    }

    // Revocamos el URL anterior antes de cargar el nuevo PDF.
    if (pdfObjectUrlRef.current) {
      URL.revokeObjectURL(pdfObjectUrlRef.current);
      pdfObjectUrlRef.current = null;
    }

    setPdfBusy(true);
    setPdfError("");

    api.get(`/cfdi/${cfdiId}/pdf`, { responseType: "blob" })
      .then(({ data }) => {
        if (cancelled) return;
        const url = URL.createObjectURL(data);
        pdfObjectUrlRef.current = url;
        setPdfBlobUrl(url);
      })
      .catch(async (e) => {
        if (cancelled) return;
        setPdfBlobUrl("");
        const msg =
          (await detailFromAxiosError(e)) ||
          formatApiError(e.response?.data?.detail) ||
          "No se pudo cargar el PDF";
        setPdfError(msg);
      })
      .finally(() => {
        if (cancelled) return;
        setPdfBusy(false);
      });

    return () => { cancelled = true; };
  }, [cfdiRef]);

  useEffect(() => {
    return () => {
      if (pdfObjectUrlRef.current) {
        URL.revokeObjectURL(pdfObjectUrlRef.current);
        pdfObjectUrlRef.current = null;
      }
    };
  }, []);

  if (!detail) return null;
  const c = detail;
  const folioStr = [c.serie, c.folio].filter(Boolean).join("-") || "—";

  const handleDownloadPdf = () => {
    if (pdfBusy || !!pdfError || !pdfBlobUrl) return;
    const uuidSuffix = (c.uuid || "").slice(0, 8);
    const filename = `CFDI_${uuidSuffix || cfdiRef || c.id || "cfdi"}.pdf`;
    const a = document.createElement("a");
    a.href = pdfBlobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Dialog open={!!detail} onOpenChange={(v)=>!v && onClose()}>
      <DialogContent className="max-w-[95vw] w-[1200px] max-h-[90vh] overflow-hidden flex flex-col rounded-sm" data-testid="cfdi-detail-dialog">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between pr-8">
            <DialogTitle style={{fontFamily:"'Chivo'"}}>Detalle CFDI · {tipoLabel(c.tipo_comprobante)}</DialogTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownloadPdf}
              disabled={pdfBusy || !!pdfError || !pdfBlobUrl}
              className="h-8 text-xs"
            >
              <FileDown className="w-3.5 h-3.5 mr-1.5" /> Descargar PDF
            </Button>
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden flex gap-6 p-1">
          {/* Columna Izquierda: Datos */}
          <div className="flex-1 overflow-y-auto space-y-6 pr-2 custom-scrollbar">
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-4 text-xs">
                <Field label="UUID" value={c.uuid} mono/>
                <Field label="Serie / Folio" value={folioStr} mono/>
                <Field label="Estado" value={c.estado || "Vigente"}/>
                <Field label="Fecha emisión" value={(c.fecha || "").replace("T", " ").slice(0,16)} mono/>
                <Field label="Método pago" value={c.metodo_pago || "—"} mono/>
                <Field label="Forma pago" value={c.forma_pago || "—"} mono/>
                <Field label="RFC Emisor" value={`${c.rfc_emisor} · ${c.nombre_emisor}`} mono/>
                <Field label="RFC Receptor" value={`${c.rfc_receptor} · ${c.nombre_receptor}`} mono/>
                <div className="col-span-3 flex flex-wrap gap-2 pt-1">
                  <IdentityValidateButton rfc={c.rfc_emisor} label="Validar RFC emisor" />
                  <IdentityValidateButton rfc={c.rfc_receptor} label="Validar RFC receptor" />
                </div>
                <Field label="Versión" value={c.version} mono/>
                <Field label="Subtotal" value={fmtMoney(c.subtotal)} right/>
                <Field label="IVA trasladado" value={fmtMoney(c.iva_trasladado)} right/>
                <Field label="Total" value={fmtMoney(c.total)} right bold/>
              </div>

              {c.tipo_comprobante === "P" && (c.pagos?.length > 0) && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Complemento de Pagos</div>
                  {(c.pagos || []).map((p, i) => (
                    <div key={i} className="border border-slate-200 rounded-sm mb-2">
                      <div className="bg-slate-50 px-3 py-2 grid grid-cols-3 gap-3 text-xs">
                        <Field label="Fecha pago" value={(p.fecha_pago || "").replace("T"," ").slice(0,16)} mono/>
                        <Field label="Monto" value={fmtMoney(p.monto)} right/>
                        <Field label="Forma" value={p.forma_pago || "—"} mono/>
                      </div>
                      <table className="w-full text-xs">
                        <thead className="bg-white">
                          <tr>
                            <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Factura relacionada (UUID)</th>
                            <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Serie/Folio</th>
                            <th className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Parc.</th>
                            <th className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Pagado</th>
                            <th className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Saldo insoluto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(p.doctos || []).map((d, j) => (
                            <tr key={j} className="border-t border-slate-100">
                              <td className="px-3 py-1.5 font-mono text-[10px]">{d.uuid}</td>
                              <td className="px-3 py-1.5 font-mono">{[d.serie, d.folio].filter(Boolean).join("-") || "—"}</td>
                              <td className="px-3 py-1.5 text-right num">{d.parcialidad}</td>
                              <td className="px-3 py-1.5 text-right num">{fmtMoney(d.imp_pagado)}</td>
                              <td className="px-3 py-1.5 text-right num">{fmtMoney(d.imp_saldo_insoluto)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}

              {c.tipo_comprobante !== "P" && c.pagos_recibidos?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Pagos recibidos / aplicados a esta factura</div>
                  <div className="border border-slate-200 rounded-sm overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-1.5 text-left text-[10px] uppercase text-slate-500 font-semibold">UUID Pago</th>
                          <th className="px-3 py-1.5 text-left text-[10px] uppercase text-slate-500 font-semibold">Fecha pago</th>
                          <th className="px-3 py-1.5 text-right text-[10px] uppercase text-slate-500 font-semibold">Parc.</th>
                          <th className="px-3 py-1.5 text-right text-[10px] uppercase text-slate-500 font-semibold">Importe pagado</th>
                          <th className="px-3 py-1.5 text-right text-[10px] uppercase text-slate-500 font-semibold">Saldo insoluto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.pagos_recibidos.map((p, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-3 py-1.5 font-mono text-[10px]">{p.pago_uuid}</td>
                            <td className="px-3 py-1.5 font-mono">{(p.fecha_pago||"").replace("T"," ").slice(0,16)}</td>
                            <td className="px-3 py-1.5 text-right num">{p.parcialidad}</td>
                            <td className="px-3 py-1.5 text-right num">{fmtMoney(p.imp_pagado)}</td>
                            <td className="px-3 py-1.5 text-right num">{fmtMoney(p.imp_saldo_insoluto)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Columna Derecha: Vista Previa PDF */}
          <div className="w-[550px] flex-shrink-0 border-l border-slate-200 pl-6 flex flex-col">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-2">
              <FileSearch className="w-3 h-3" /> Representación Impresa (Vista Previa)
            </div>
            <div className="flex-1 bg-slate-100 rounded-sm border border-slate-200 overflow-hidden relative">
              {pdfBusy && (
                <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-600 bg-slate-100/80 z-10">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cargando PDF...
                </div>
              )}

              {!pdfBusy && pdfError && (
                <div className="absolute inset-0 overflow-auto p-4 text-xs text-rose-700 bg-white z-10">
                  <div className="font-semibold mb-2">No se pudo cargar el PDF</div>
                  <div className="whitespace-pre-wrap break-all">{pdfError}</div>
                  <div className="mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openCfdiPdfInNewTab(cfdiRef)}
                      className="h-7 text-xs"
                    >
                      Abrir en pestaña
                    </Button>
                  </div>
                </div>
              )}

              {!pdfBusy && !pdfError && pdfBlobUrl && (
                <iframe
                  src={`${pdfBlobUrl}#toolbar=0&navpanes=0&view=FitH`}
                  className="w-full h-full border-0"
                  title="Vista previa CFDI"
                />
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, mono, right, bold }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${right ? "text-right" : ""} ${bold ? "font-bold" : ""} text-slate-800 mt-0.5 break-all`}>{value || "—"}</div>
    </div>
  );
}
